
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, gql, useMutation} from '@apollo/client';
import throttle from 'lodash.throttle';
import {
  Input,
  Divider,
  Accordion,
  Dropdown,
  Button,
  Dimmer,
  Loader,
  Icon,
  Container,
  Header,
  Segment,
} from 'semantic-ui-react'
import DatePicker from "react-datepicker";

import { PATHS } from '../../../../paths';
import { formatDateTime, formatDateTimeToDay } from "../../../../dates";

import styles from './AdminClassView.module.css';

import { AttendanceGraph } from "../../../../components/Protected/AttendanceGraph";
import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { Session } from '../../../../components/Session';
import { useIsAdmin } from "../../../../hooks/isAdmin";

import { CourseAdmin } from '../CourseAdmin';
import { CourseTags } from '../CourseTags';

const GET_COURSE = gql`
  query Courses($courseId: Int!) {
    course(courseId: $courseId) {
      id
      name
      canEdit
      SKU
      attendancePercentage
      overseers {
          id
          name
      }
      courseParticipantCount
      tags {
        id
        name
      }
      futureParticipants {
          id
          name
      }
      sessions {
        id
        course {
            id
            name
            SKU
        }
        durationMinutes
        attendance {
            details
            status
            id
            isTeacher
            user {
                name
                id
                identifier
                photoUrl
            }
        }
        startTime
        attendanceCount
        reasonForCancellation
        attendancePercentage
      }
    }
  }`;

const ADD_PERSON_TO_SESSION = gql`
mutation AddPersonToSession($sessionId: Int!, $identifier: String, $status: String) {
  addPersonToSession(sessionId: $sessionId, identifier: $identifier, status: $status) {
    id
  }
}`;

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

const ADD_SESSION_TO_COURSE = gql`
mutation AddSession($courseId: Int!, $date: String) {
  addSession(courseId: $courseId, date: $date) {
    id
  }
}
`;

const UPDATE_SESSION_TIME = gql`
mutation UpdateSessionTime($sessionId: Int!, $magnitude: Int!, $forwardDirection: Boolean!, $unit: String!) {
  updateSessionTime(sessionId: $sessionId, magnitude: $magnitude, forwardDirection: $forwardDirection, unit: $unit) {
    id
  }
}`;

const DUPLICATE_COURSE = gql`
  mutation Mutation($courseToCopyId: Int!, $newCourseName: String!, $daysToCopyItForward: Int!, $newBlockTagId: Int!, $oldBlockTagId: Int!) {
    duplicateCourse(courseToCopyId: $courseToCopyId, newCourseName: $newCourseName, daysToCopyItForward: $daysToCopyItForward, newBlockTagId: $newBlockTagId, oldBlockTagId: $oldBlockTagId) {
      status
    }
  }`;

const DELETE_COURSE = gql`
  mutation Mutation($courseId: Int!) {
    deleteCourse(courseId: $courseId) {
      status
    }
  }`;

const RENAME_COURSE = gql`
  mutation RenameCourse($courseId: Int!, $newCourseName: String!) {
    renameCourse(courseId: $courseId, newCourseName: $newCourseName) {
      status
    }
  }`


const CANCELLATION_REASON = [
  {
    key: 'Teacher-sick',
    text: 'Teacher sick',
    value:'Teacher sick',
  },
  {
    key: 'Teacher-holiday',
    text: 'Teacher holiday',
    value:'Teacher holiday',
  },
  {
    key: 'Company-cancelled',
    text: 'Company cancelled',
    value:'Company cancelled',
  },
  {
    key: 'Other',
    text: 'Other',
    value:'Other',
  }
]

const AdminClassView = () => {
  let navigate = useNavigate();
  let { id } = useParams();
  const isAdmin = useIsAdmin();

  const [disableDuplicate, setDisableDuplicate] = useState(false);
  const [newCourseNameDuplicate, setNewCourseNameDuplicate] = useState('');
  const [newCourseName, setNewCourseName] = useState('');
  const [daysToCopyItForward, setDaysToCopyItForward] = useState(0);
  const { loading, error, data, refetch } = useQuery(GET_COURSE, { variables: { courseId: Number(id) } });

  const [addPersonToSession] = useMutation(ADD_PERSON_TO_SESSION);
  const [deleteSession] = useMutation(DELETE_SESSION);

  const [newSessionDate, setNewSessionDate] = useState<Date | null>(new Date());
  const [addSessionToCourse] = useMutation(ADD_SESSION_TO_COURSE);
  const [updateSessionTime] = useMutation(UPDATE_SESSION_TIME);
  const [duplicateClass] = useMutation(DUPLICATE_COURSE);
  const [renameCourse] = useMutation(RENAME_COURSE);
  const [deleteCourse] = useMutation(DELETE_COURSE);

  const [editMode, setEditMode] = useState(false);
  const [activeId, setActiveId] = useState();
  const [identifier, setIdentifier] = useState('');
  const [hasUsedAccordian, setHasUsedAccordian] = useState(false);

  const TenHoursAgo = Number(new Date()) - 10*60*60*1000;

  const updateCourseName = useCallback(throttle(async (newName: string) => {
      renameCourse({ variables: { courseId: Number(id), newCourseName: newName } })
      refetch();
  }, 500, { leading: false, trailing: true }), [renameCourse]);

  useEffect(() => {
    if (!activeId && !loading && !error && data != null && data.course) {
      const nextSession = data.course.sessions.find(
        (s: {startTime:number, reasonForCancellation: string | null}) =>
              s.startTime > TenHoursAgo
          && (s.reasonForCancellation == null || s.reasonForCancellation === ""))

      if (nextSession != null && hasUsedAccordian === false) {
        setActiveId(nextSession.id)
      }
    }
  }, [loading, error, data, activeId, hasUsedAccordian])

  if (loading) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
            <Dimmer active inverted>
              <Loader inverted content='Loading' />
            </Dimmer>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  // check for no internet connection
  if (error && error.message === 'Failed to fetch') {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                No internet connection
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (!data || !data.course) {
    return (
      <ResponsiveContainer>
        <Segment style={{ padding: '0em 0em' }} vertical>
          <Container>
              <Header as='h2'>
                Class not found
              </Header>
          </Container>
        </Segment>
      </ResponsiveContainer>
    )
  }

  if (error) {
    return <div />
  }

  // TODO: move Info section to its own component
  return (
    <ResponsiveContainer>
      <Segment key={id} style={{ padding: '2em 0em' }} vertical>
        <Container>
          <div className={styles.backRow}>
            <Icon name='arrow left' size='large' style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          </div>

          {/* Title + primary actions */}
          <div className={styles.titleRow}>
            {editMode && isAdmin ? (
              <Input
                placeholder='New class name'
                value={newCourseName}
                onChange={async (e: any) => {
                  setNewCourseName(e.target.value)
                  await updateCourseName(e.target.value)
                }}
              />
            ) : (
              <span className={styles.title}>{data?.course?.name}</span>
            )}

            {isAdmin && (
              <div className={styles.actions}>
                <Button
                  onClick={() => {
                    setEditMode(!editMode)
                    setNewCourseName(data && data.course && data.course.name)
                  }}
                >
                  <Icon name='edit' /> {editMode ? "Done editing" : "Edit"}
                </Button>
                <Button onClick={() => navigate(`${PATHS.CLASSES}/${id}/records`)}>
                  <Icon name='file alternate' /> Records
                </Button>
              </div>
            )}
          </div>

          {/* Edit-mode admin panel: duplicate + delete */}
          {isAdmin && editMode && (
            <Segment secondary className={styles.adminPanel}>
              <Header as='h4'>Duplicate class for next block</Header>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>New class name</label>
                <Input
                  fluid
                  placeholder='New class name'
                  value={newCourseNameDuplicate}
                  onChange={(e: any) => setNewCourseNameDuplicate(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Days to copy forward</label>
                <Input
                  fluid
                  placeholder='Days to copy it forward'
                  value={daysToCopyItForward}
                  onChange={(e: any) => {
                    const value = Number(e.target.value)
                    if (value < 0 || isNaN(value)) {
                      return
                    }
                    setDaysToCopyItForward(Number(e.target.value))
                  }}
                />
              </div>
              <p className={styles.muted}>
                First class: {data && data.course && data.course.sessions.length > 0 && formatDateTime(Number(data.course.sessions[0].startTime) + daysToCopyItForward * 24 * 60 * 60 * 1000)}
              </p>
              <Button
                primary
                disabled={disableDuplicate || newCourseNameDuplicate === '' || daysToCopyItForward === 0}
                onClick={async () => {
                  await duplicateClass({ variables: {
                    courseToCopyId: Number(id),
                    newCourseName: newCourseNameDuplicate,
                    daysToCopyItForward,
                    newBlockTagId: 1774,
                    oldBlockTagId: 1505,
                  }});
                  setDisableDuplicate(true)
                }}
              >
                Duplicate for next block
              </Button>

              <Divider />

              <Button
                color='red'
                basic
                icon='trash'
                content='Delete class'
                onClick={async () => {
                  if (window.confirm("Really delete this? (Use cancel to back out)")) {
                    await deleteCourse({ variables: { courseId: Number(id) }});
                    navigate(-1)
                  } else {
                    console.log('Ok we are backing out go')
                  }
                }}
              />
            </Segment>
          )}
          <Divider horizontal>
            <Header as='h4'>
              Info
            </Header>
          </Divider>
          <AttendanceGraph data={data} />

          <div className={styles.infoGrid}>
            <span className={styles.infoLabel}>SKU</span>
            <span className={styles.infoValue}>{data && data.course && data.course.SKU}</span>

            <span className={styles.infoLabel}>Teachers</span>
            <span className={styles.infoValue}>
              {data && data.course && data.course.overseers.map((o: any) => (
                <Link key={o.id} to={`${PATHS.PEOPLE}/${o.id}`}>{o.name}</Link>
              )).reduce((prev: any, curr: any) => [prev, ', ', curr], [])}
            </span>

            <span className={styles.infoLabel}>Attendance</span>
            <span className={styles.infoValue}>{data && data.course && Math.round(data.course.attendancePercentage * 100)}%</span>

            <span className={styles.infoLabel}>Participants</span>
            <span className={styles.infoValue}>{data && data.course && data.course.courseParticipantCount}</span>

            <span className={styles.infoLabel}>Tags</span>
            <span className={styles.infoValue}>
              <CourseTags tags={data && data.course && data.course.tags} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
            </span>
          </div>
          {editMode && <CourseAdmin participants={(data && data.course && data.course.futureParticipants) || []} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />}
          <Divider horizontal>
            <Header as='h4'>
              Sessions
            </Header>
          </Divider>
          {loading && (<Dimmer active inverted>
              <Loader inverted content='Loading' />
          </Dimmer>)
          }
          <Accordion style={{ marginBottom: '3em' }}>
            {!loading && !error && data != null && data.course && data.course.sessions.map((s: any) => {

              if (!editMode && s.reasonForCancellation != null && s.reasonForCancellation === 'Other') {
                return null; // Skip sessions that are cancelled with 'Other' reason
              }

              const inFuture = s.startTime > Number(new Date())

              const adjustTime = (magnitude: number, forwardDirection: boolean, unit: string) => async (e: any) => {
                e.stopPropagation()
                await updateSessionTime({ variables: { sessionId: s.id, magnitude, forwardDirection, unit } });
                refetch({ variables: { courseId: Number(id) } })
              }

              return (
              <Fragment key={s.id}>
                <Accordion.Title
                  style={s.startTime > TenHoursAgo ? {}: { opacity: "0.51"}}
                  active={activeId === s.id}
                  index={0}
                  onClick={() => {
                    if (s.id !== activeId) {
                      setIdentifier('');
                      setActiveId(s.id)
                    } else {
                      setActiveId(undefined)
                    }
                    setHasUsedAccordian(true)
                  }}
                >
                {s.reasonForCancellation != null ? (
                  <b>Class cancelled on {formatDateTimeToDay(s.startTime)}</b>
                ) : (
                  <div className={styles.sessionTitle}>
                    <span className={styles.sessionMeta}>
                      <Icon name='dropdown' />
                      <Icon name='user' /> {s.attendanceCount}
                    </span>
                    <b>{formatDateTime(s.startTime)}</b>
                    {!inFuture ? `(attendance: ${Math.round(s.attendancePercentage*100)}%)` : ""}
                    {isAdmin && editMode && (
                      <span className={styles.sessionControls}>
                        <Button size='mini' basic color='red' onClick={async (e:any) => {
                          e.stopPropagation()
                          await deleteSession({ variables: { sessionId: s.id, reason: 'Teacher sick'} })
                          refetch({ variables: { courseId: Number(id) } })
                        }}>Cancel session</Button>
                        <Button.Group size='mini' basic>
                          <Button onClick={adjustTime(1, false, "days")}>-1d</Button>
                          <Button onClick={adjustTime(1, false, "hours")}>-1h</Button>
                          <Button onClick={adjustTime(15, true, "minutes")}>+15m</Button>
                          <Button onClick={adjustTime(1, true, "hours")}>+1h</Button>
                          <Button onClick={adjustTime(1, true, "days")}>+1d</Button>
                        </Button.Group>
                      </span>
                    )}
                  </div>
                )}
                </Accordion.Title>
                {s.reasonForCancellation != null ? (
                  isAdmin && editMode ? (
                    <Dropdown
                        placeholder='Reason...'
                        selection
                        value={s.reasonForCancellation}
                        onChange={async (_, d) =>  {
                          await deleteSession({ variables: { sessionId: s.id, reason: d.value} })
                          setTimeout(() => refetch({ courseId: Number(id) }), 2000)
                        }}
                        options={CANCELLATION_REASON}
                    />
                  ) : (
                    <div className={styles.muted}>Reason: {s.reasonForCancellation}</div>
                  )
                ) : (
                  <Accordion.Content active={activeId === s.id}>
                    <Session canEditCourse={data && data.course && data.course.canEdit} session={s as any} refetchCourse={() => refetch({ variables: { courseId: Number(id) } })} />
                    {isAdmin && editMode && (
                      <div className={styles.addPersonRow}>
                        <Input
                            placeholder='StudentId to add'
                            value={identifier.toLocaleUpperCase()}
                            onKeyPress={async (e: any) => {
                               if (e.key === 'Enter') {
                                  await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                                  refetch({ variables: { courseId: Number(id) } })
                                  setIdentifier('')
                               }
                            }}
                            onChange={(e: any) => setIdentifier(e.target.value)}
                        />
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PENDING' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add</Button>
                        <Button onClick={async () => {
                          await addPersonToSession({ variables: { sessionId: s.id, identifier, status: 'PRESENT' } })
                          refetch({ variables: { courseId: Number(id) } })
                          setIdentifier('')
                        }}>Add as Present</Button>
                      </div>
                    )}
                  </Accordion.Content>
                )}
              </Fragment>
            )
            })
          }
          </Accordion>
            {isAdmin && editMode && (
              <div className={styles.addSessionRow}>
                <DatePicker
                    selected={newSessionDate}
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => {
                        if (date == null) return;
                        setNewSessionDate(date);
                    }}
                    showYearDropdown
                    maxDate={new Date(new Date().getFullYear() + 1, new Date().getMonth(), new Date().getDate())}
                />
                <Button
                  onClick={async () => {
                    await addSessionToCourse({ variables: { courseId: Number(id), date: newSessionDate?.toISOString() } })
                    // move new session date forward 7 days
                    if (newSessionDate == null) return;
                    const newDate = new Date(newSessionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                    setNewSessionDate(newDate);
                    refetch({ variables: { courseId: Number(id) } })
                  }}
                >
                  Add session
                </Button>
              </div>
            )}
        </Container>
      </Segment>

    </ResponsiveContainer>
  );
}

export { AdminClassView }
